import React from "react";
import moment from "moment";
import { Empty } from "antd";
import { cnpjMask } from "../../../services/helpersMethodsService";
import {
  DanfeBox,
  Section,
  SectionTitle,
  FieldsRow,
  Field,
  ItemsTable,
} from "./styles";

const fmtMoney = (value) =>
  (value || 0).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });

const fmtQtd = (value) =>
  (value || 0).toLocaleString("pt-BR", {
    minimumFractionDigits: 4,
    maximumFractionDigits: 4,
  });

const fmtDate = (value) => {
  if (!value) return "-";
  const date = moment(value);
  return date.isValid() ? date.format("DD/MM/YYYY") : value;
};

const fmtChave = (value) => {
  if (!value) return "-";
  return value.replace(/(.{4})/g, "$1 ").trim();
};

const FieldItem = ({ label, value, grow, bold }) => (
  <Field grow={grow} bold={bold}>
    <div className="fieldLabel">{label}</div>
    <div className="fieldValue">{value || "-"}</div>
  </Field>
);

const DanfeContent = ({ invoice, t }) => {
  if (!invoice) {
    return <Empty description={false} />;
  }

  const items = Array.isArray(invoice.itens) ? invoice.itens : [];

  return (
    <DanfeBox>
      <SectionTitle>{t.emitter.title}</SectionTitle>
      <Section>
        <FieldsRow>
          <FieldItem
            grow={2}
            label={t.emitter.razaoSocial}
            value={invoice.emit_razao_social}
          />
          <FieldItem
            label={t.emitter.nomeFantasia}
            value={invoice.emit_nome_fantasia}
          />
          <FieldItem
            label={t.emitter.cnpj}
            value={invoice.emit_cnpj ? cnpjMask(invoice.emit_cnpj) : null}
          />
        </FieldsRow>
        <FieldsRow>
          <FieldItem label={t.emitter.ie} value={invoice.emit_ie} />
          <FieldItem
            label={t.emitter.municipioUf}
            value={
              invoice.emit_municipio
                ? `${invoice.emit_municipio}/${invoice.emit_uf || ""}`
                : null
            }
          />
          <FieldItem
            label={t.identification.accessKey}
            value={fmtChave(invoice.chave_acesso)}
          />
        </FieldsRow>
      </Section>

      <SectionTitle>{t.identification.title}</SectionTitle>
      <Section>
        <FieldsRow>
          <FieldItem
            label={t.identification.number}
            value={invoice.numero_nf}
          />
          <FieldItem label={t.identification.series} value={invoice.serie} />
          <FieldItem
            label={t.identification.issueDate}
            value={fmtDate(invoice.data_emissao)}
          />
          <FieldItem
            label={t.identification.exitEntryDate}
            value={fmtDate(invoice.data_saida_entrada)}
          />
        </FieldsRow>
        <FieldsRow>
          <FieldItem
            grow={2}
            label={t.identification.operationNature}
            value={invoice.natureza_operacao}
          />
          <FieldItem
            label={t.identification.protocol}
            value={invoice.protocolo}
          />
          <FieldItem
            label={t.identification.status}
            value={invoice.status_sefaz}
          />
        </FieldsRow>
      </Section>

      <SectionTitle>{t.recipient.title}</SectionTitle>
      <Section>
        <FieldsRow>
          <FieldItem
            grow={2}
            label={t.recipient.razaoSocial}
            value={invoice.dest_razao_social}
          />
          <FieldItem
            label={t.recipient.cnpj}
            value={invoice.dest_cnpj ? cnpjMask(invoice.dest_cnpj) : null}
          />
          <FieldItem
            label={t.recipient.municipioUf}
            value={
              invoice.dest_municipio
                ? `${invoice.dest_municipio}/${invoice.dest_uf || ""}`
                : null
            }
          />
        </FieldsRow>
      </Section>

      <SectionTitle>{t.taxes.title}</SectionTitle>
      <Section>
        <FieldsRow>
          <FieldItem
            label={t.taxes.products}
            value={fmtMoney(invoice.valor_produtos)}
          />
          <FieldItem
            label={t.taxes.freight}
            value={fmtMoney(invoice.valor_frete)}
          />
          <FieldItem
            label={t.taxes.insurance}
            value={fmtMoney(invoice.valor_seguro)}
          />
          <FieldItem
            label={t.taxes.discount}
            value={fmtMoney(invoice.valor_desconto)}
          />
        </FieldsRow>
        <FieldsRow>
          <FieldItem label={t.taxes.ipi} value={fmtMoney(invoice.valor_ipi)} />
          <FieldItem
            label={t.taxes.icms}
            value={fmtMoney(invoice.valor_icms)}
          />
          <FieldItem label={t.taxes.pis} value={fmtMoney(invoice.valor_pis)} />
          <FieldItem
            label={t.taxes.cofins}
            value={fmtMoney(invoice.valor_cofins)}
          />
          <FieldItem
            label={t.taxes.others}
            value={fmtMoney(invoice.valor_outras)}
          />
          <FieldItem
            bold
            label={t.taxes.total}
            value={fmtMoney(invoice.valor_total_nf)}
          />
        </FieldsRow>
      </Section>

      <SectionTitle>{t.items.title}</SectionTitle>
      <Section>
        <ItemsTable>
          <thead>
            <tr>
              <th>{t.items.code}</th>
              <th>{t.items.description}</th>
              <th>{t.items.ncm}</th>
              <th>{t.items.cfop}</th>
              <th>{t.items.unit}</th>
              <th className="numeric">{t.items.quantity}</th>
              <th className="numeric">{t.items.unitValue}</th>
              <th className="numeric">{t.items.totalValue}</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item, index) => (
              <tr key={`${item.nItem || index}`}>
                <td>{item.cProd}</td>
                <td>{item.xProd}</td>
                <td>{item.NCM}</td>
                <td>{item.CFOP}</td>
                <td>{item.uCom}</td>
                <td className="numeric">{fmtQtd(item.qCom)}</td>
                <td className="numeric">{fmtMoney(item.vUnCom)}</td>
                <td className="numeric">{fmtMoney(item.vProd)}</td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr>
                <td className="empty" colSpan={8}>
                  {t.items.empty}
                </td>
              </tr>
            )}
          </tbody>
        </ItemsTable>
      </Section>
    </DanfeBox>
  );
};

export default DanfeContent;
